import React, { createContext, useContext, useState, useCallback } from 'react';

const BrandingContext = createContext();

const CACHE_KEY = 'si_branding_cache';

// Default branding (fallback to Smart Installers)
const DEFAULT_BRANDING = {
  isWhiteLabeled: false,
  companyName: 'Smart Installers',
  logoUrl: '/l_white.png',
  subtitle: 'Device Monitoring Platform',
  logoBgColor: '#164da6',
  logoScale: 'medium'
};

const getCachedBranding = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.companyName) return parsed;
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

const saveBrandingCache = (branding) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
  } catch (e) {
    // Ignore storage errors
  }
};

const clearBrandingCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (e) {
    // Ignore storage errors
  }
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children, initialBranding = null }) => {
  const [branding, setBranding] = useState(() => {
    if (initialBranding) return initialBranding;
    return getCachedBranding() || DEFAULT_BRANDING;
  });
  const [loading, setLoading] = useState(() => {
    if (initialBranding) return false;
    return !getCachedBranding();
  });

  const finishLoading = useCallback(() => {
    setLoading(false);
  }, []);

  const updateBranding = useCallback((newBranding) => {
    if (newBranding && newBranding.is_white_labeled && newBranding.company_name && newBranding.logo_url) {
      const transformed = {
        isWhiteLabeled: true,
        companyName: newBranding.company_name,
        logoUrl: newBranding.logo_url,
        subtitle: newBranding.subtitle || 'Device Monitoring Platform',
        logoBgColor: newBranding.logo_bg_color || '#164da6',
        logoScale: newBranding.logo_scale || 'medium'
      };
      setBranding(transformed);
      saveBrandingCache(transformed);
    } else {
      setBranding(DEFAULT_BRANDING);
      saveBrandingCache(DEFAULT_BRANDING);
    }
    setLoading(false);
  }, []);

  const resetBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    clearBrandingCache();
    setLoading(false);
  }, []);

  const value = {
    branding,
    updateBranding,
    resetBranding,
    finishLoading,
    loading,
    DEFAULT_BRANDING
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export { getCachedBranding };
export default BrandingContext;
